import { useState, useEffect, useRef } from "react"
import { CONVERSATION_NAME_MAX, CONVERSATION_NAME_MIN, flattenPastedName } from "./name-limits"
import { X, Camera, Users, Trash2 } from "lucide-react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { AvatarPhoto } from "./avatar-photo"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { acceptAttributeFor, checkFileForUpload, getUploadPolicy } from "@/lib/upload-policy"

interface EditGroupPageProps {
  groupId: string
  currentName: string
  currentImage?: string
  currentDescription?: string
  onClose: () => void
  onSave: (data: { name: string; imageUrl?: string; imageFile?: File; description?: string; imageRemoved: boolean }) => void
}

export function EditGroupPage({
  groupId: _groupId,
  currentName,
  currentImage,
  currentDescription,
  onClose,
  onSave,
}: EditGroupPageProps) {
  const t = useTranslation()
  const [name, setName] = useState(currentName)
  const [description, setDescription] = useState(currentDescription || "")
  const [selectedImage, setSelectedImage] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined)
  // Picker filter from the server's policy; any image until it loads.
  const [imageAccept, setImageAccept] = useState("image/*")
  useEffect(() => {
    void getUploadPolicy().then(() => setImageAccept(acceptAttributeFor("image") || "image/*"))
  }, [])
  const [imageRemoved, setImageRemoved] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [nameError, setNameError] = useState("")
  const fileInputRef = useRef<HTMLInputElement>(null)
  const nameFieldRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const nameChanged = name.trim() !== currentName
    const descChanged = description.trim() !== (currentDescription || "")
    const imageChanged = selectedImage !== null || imageRemoved
    setHasChanges(nameChanged || descChanged || imageChanged)
  }, [name, description, selectedImage, imageRemoved, currentName, currentDescription])

  // Grows the name box to fit what's loaded or typed, rather than scrolling
  // it sideways inside a fixed single line. This is also where an existing
  // 100-character name (the field's own limit) is shown on open.
  useEffect(() => {
    const el = nameFieldRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [name])

  const displayImage = imageRemoved ? null : selectedImage || currentImage

  const handleSave = () => {
    const trimmed = name.trim()
    if (trimmed.length > CONVERSATION_NAME_MAX) {
      setNameError(`Group name must be ${CONVERSATION_NAME_MAX} characters or fewer`)
      return
    }
    if (trimmed.length < CONVERSATION_NAME_MIN) {
      setNameError('Group name must be at least 2 characters')
      return
    }
    onSave({
      name: trimmed,
      imageUrl: selectedImage || undefined,
      imageFile: selectedFile,
      description: description.trim() || undefined,
      imageRemoved,
    })
  }

  const handlePickImage = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return
    const check = await checkFileForUpload(file, "image")
    if (!check.ok) {
      toast.error({ title: check.message, description: file.name })
      return
    }
    setSelectedFile(file)
    const reader = new FileReader()
    reader.onload = (ev) => {
      setSelectedImage(ev.target?.result as string)
      setImageRemoved(false)
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveImage = () => {
    setSelectedImage(null)
    setSelectedFile(undefined)
    setImageRemoved(true)
  }

  return (
    <div className="flex flex-col h-full bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-4 border-b border-border shrink-0">
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <h2 className="flex-1 text-lg font-semibold text-foreground">Edit Group</h2>
        <button
          onClick={handleSave}
          disabled={!hasChanges}
          className="px-4 py-1.5 rounded-lg font-medium text-sm transition-colors disabled:text-muted-foreground text-primary hover:bg-primary/10 disabled:hover:bg-transparent"
        >
          Save
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Group Photo */}
        <div className="flex flex-col items-center mb-6">
          <div
            onClick={handlePickImage}
            className="relative cursor-pointer"
          >
            <div className="w-28 h-28 rounded-full bg-muted flex items-center justify-center overflow-hidden">
              <AvatarPhoto
                src={displayImage}
                alt="Group"
                className="w-full h-full object-cover"
                fallback={<Users className="w-12 h-12 text-muted-foreground" />}
              />
            </div>
            <div className="absolute -bottom-1 -right-1 w-9 h-9 bg-primary rounded-full border-4 border-background flex items-center justify-center">
              <Camera className="w-4 h-4 text-white" />
            </div>
          </div>
          <button
            onClick={handlePickImage}
            className="mt-3 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            {/* No picture yet vs. replacing an existing one are different asks. */}
            {displayImage ? t('presence.changePhoto') : t('presence.addPhoto')}
          </button>
        </div>

        {/* Group Name */}
        <div className="mb-6">
          <label className="block text-xs font-medium text-muted-foreground mb-2">Group Name</label>
          <textarea
            ref={nameFieldRef}
            value={name}
            onChange={e => {
              setName(flattenPastedName(e.target.value).slice(0, CONVERSATION_NAME_MAX))
              if (nameError) setNameError("")
            }}
            onKeyDown={e => { if (e.key === 'Enter') e.preventDefault() }}
            maxLength={CONVERSATION_NAME_MAX}
            placeholder="Enter group name"
            rows={1}
            className={`w-full rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none border resize-none overflow-hidden [overflow-wrap:anywhere] ${nameError ? 'border-destructive bg-destructive/5' : 'border-none bg-muted'}`}
          />
          <div className="flex justify-between mt-1">
            {nameError ? <p className="text-xs text-destructive">{nameError}</p> : <span />}
            <span className={`text-xs ${name.length >= CONVERSATION_NAME_MAX ? 'text-destructive' : 'text-muted-foreground'}`}>
              {name.length}/{CONVERSATION_NAME_MAX}
            </span>
          </div>
        </div>

        {/* Group Description */}
        <div className="mb-6">
          <label className="block text-xs font-medium text-muted-foreground mb-2">Description (optional)</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            maxLength={200}
            rows={4}
            placeholder="Add a description for the group"
            className="w-full bg-muted rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none border-none resize-none"
          />
          <p className="text-xs text-muted-foreground mt-2">
            {description.length}/200
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Only group members can see the description
          </p>
        </div>

        {/* Remove Photo Option */}
        {displayImage && (
          <div className="mt-6">
            <button
              onClick={handleRemoveImage}
              className="flex items-center gap-2 text-destructive hover:text-destructive/80 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              <span className="text-sm font-medium">Remove Photo</span>
            </button>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={imageAccept}
          className="hidden"
          onChange={handleFileChange}
        />
      </div>
    </div>
  )
}
